import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Play,
  History,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  LogOut,
  UserCircle,
  Clock,
  BarChart3
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * Facilitator Dashboard Wrapper with role-specific navigation
 * Separate from AdminDashboardWrapper for clear role separation
 */
function FacilitatorDashboardWrapper({ children }) {
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('facilitatorSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('facilitatorSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const navItems = [
    { 
      href: '/facilitator', 
      label: 'Dashboard', 
      icon: LayoutDashboard,
      description: 'Overview and quick actions'
    },
    { 
      href: '/facilitator/analytics', 
      label: 'My Analytics', 
      icon: BarChart3,
      description: 'Performance metrics'
    },
    { 
      href: '/facilitator/participants', 
      label: 'My Participants', 
      icon: Users,
      description: 'Assigned participants'
    },
    { 
      href: '/facilitator/sessions', 
      label: 'Active Sessions', 
      icon: Play,
      description: 'Sessions in progress'
    },
    { 
      href: '/facilitator/schedule', 
      label: 'Schedule', 
      icon: Calendar,
      description: 'Upcoming sessions'
    },
    { 
      href: '/facilitator/history', 
      label: 'Session History', 
      icon: History,
      description: 'Past sessions'
    },
  ];

  const NavItem = ({ item, collapsed }) => {
    const Icon = item.icon;
    const isActive = window.location.pathname === item.href || 
                     (item.href !== '/facilitator' && window.location.pathname.startsWith(item.href));

    const content = (
      <a
        href={item.href}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200",
          isActive 
            ? "bg-primary text-primary-foreground shadow-sm" 
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
          collapsed && "justify-center px-2"
        )}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-primary-foreground")} />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </a>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {content}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            <p>{item.label}</p>
            <p className="text-xs text-muted-foreground">{item.description}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  const SidebarContent = ({ collapsed, isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo Area */}
      <div className={cn(
        "flex items-center gap-3 px-4 py-5 border-b border-border",
        collapsed && "justify-center px-2"
      )}>
        {collapsed ? (
          <a href="/facilitator" className="flex items-center">
            <Logo variant="icon" size="sm" />
          </a>
        ) : (
          <a href="/facilitator" className="flex items-center gap-2" data-testid="facilitator-logo">
            <Logo variant="full" size="sm" />
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 rounded-full">
              Facilitator
            </span>
          </a>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <TooltipProvider>
          <div className="space-y-1">
            {!collapsed && (
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Navigation
              </div>
            )}
            {navItems.map((item) => (
              <NavItem key={item.href} item={item} collapsed={collapsed} />
            ))}
          </div>

          {/* Quick Stats (only when expanded) */}
          {!collapsed && (
            <div className="mt-8 px-3">
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  Today's Overview
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sessions Today</span>
                    <span className="font-medium">0</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Active Now</span>
                    <span className="font-medium text-green-600">0</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Back to Admin (if user is also admin) */}
          {user?.role === 'admin' && (
            <div className="mt-4 px-3">
              {collapsed ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <a
                      href="/admin"
                      className="flex items-center justify-center p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                    >
                      <UserCircle className="w-5 h-5" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="right">Switch to Admin</TooltipContent>
                </Tooltip>
              ) : (
                <a
                  href="/admin"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  <UserCircle className="w-4 h-4" />
                  Switch to Admin
                </a>
              )}
            </div>
          )}
        </TooltipProvider>
      </nav>

      {/* User Section */}
      <div className={cn(
        "border-t border-border p-4 space-y-3",
        collapsed && "px-2"
      )}>
        {!collapsed && (
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'F'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.firstName || 'Facilitator'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
        )}
        
        <div className={cn("flex items-center gap-2", collapsed ? "flex-col" : "px-2")}>
          <ThemeToggle />
          {collapsed ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={logout}
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                    data-testid="facilitator-logout-button"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Logout</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              data-testid="facilitator-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 -ml-2 text-muted-foreground hover:text-foreground"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <a href="/facilitator" className="flex items-center gap-2">
            <Logo variant="full" size="sm" />
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 rounded-full">
              Facilitator
            </span>
          </a>
          <div className="w-10" /> {/* Spacer for alignment */}
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div 
            className="absolute left-0 top-0 bottom-0 w-72 bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent collapsed={false} isMobile={true} />
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside 
          className={cn(
            "hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40 bg-card border-r border-border transition-all duration-300 ease-in-out",
            sidebarCollapsed ? "w-[72px]" : "w-64"
          )}
        >
          <SidebarContent collapsed={sidebarCollapsed} />
          
          {/* Collapse Toggle Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-20 p-1.5 bg-card border border-border rounded-full shadow-md hover:bg-muted transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
        </aside>

        {/* Main Content */}
        <main 
          className={cn(
            "flex-1 min-h-screen transition-all duration-300 ease-in-out",
            sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-64"
          )}
        >
          {/* Desktop Top Bar */}
          <header className="hidden lg:flex items-center justify-between sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 px-6 py-3">
            <div className="text-sm text-muted-foreground">
              {/* Page title or breadcrumb could go here */}
            </div>
            <div className="flex items-center gap-3">
              {/* Future: Notification bell for facilitators */}
            </div>
          </header>

          {/* Page Content */}
          <div className="p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default FacilitatorDashboardWrapper;
